import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmployeeForm } from '@/features/people/components/employee-form';
import { useCandidate } from '@/hooks/use-candidates';
import { useConvertCandidateToEmployee, useVacancy } from '@/hooks/use-recruitment';
import type { CreateEmployeeInput } from '@/lib/employees';
import { extractErrorMessage } from '@/lib/errors';

export interface HireCandidateInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

interface HireCandidateDialogProps {
  open: boolean;
  lockedVacancyId?: string;
  lockedVacancyTitle?: string;
  candidate: HireCandidateInfo | null;
  onOpenChange: (open: boolean) => void;
  onHired: () => void;
  onCancelled: () => void;
}

// HW-480/HW-692/HW-646: opens from a HIRED-mapped column on the vacancy board,
// or from the candidate status popover in the profile header and the candidates
// list. Renders EmployeeForm and wires it to the convert-to-employee endpoint so
// the Employee creation and the candidate-status flip happen in one atomic call.
// The Vacancy field names which application the hire closes (HW-692).
export function HireCandidateDialog({
  open,
  lockedVacancyId,
  lockedVacancyTitle,
  candidate,
  onOpenChange,
  onHired,
  onCancelled,
}: HireCandidateDialogProps) {
  const candidateId = candidate?.id ?? '';
  const { data: detail, isLoading: candidateLoading } = useCandidate(candidateId);
  const convertMutation = useConvertCandidateToEmployee();

  const [pickedVacancyId, setPickedVacancyId] = useState('');
  const [vacancyError, setVacancyError] = useState<string | null>(null);
  const [rejectOthers, setRejectOthers] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);

  const activeAssignments = useMemo(
    () => detail?.assignments.filter((a) => a.result === 'IN_PROGRESS') ?? [],
    [detail],
  );

  // The locked vacancy may not be among the candidate's IN_PROGRESS assignments
  // (e.g. its result already flipped in a divergent cache), which would leave the
  // Select with no item matching its value and show the placeholder on a locked
  // field. Always surface an option for it, titled from the board.
  const vacancyOptions = useMemo(() => {
    const options = activeAssignments.map((a) => ({
      vacancyId: a.vacancyId,
      vacancyTitle: a.vacancyTitle,
    }));
    if (lockedVacancyId && !options.some((o) => o.vacancyId === lockedVacancyId)) {
      const known = detail?.assignments.find((a) => a.vacancyId === lockedVacancyId);
      options.unshift({
        vacancyId: lockedVacancyId,
        vacancyTitle: lockedVacancyTitle ?? known?.vacancyTitle ?? 'Selected vacancy',
      });
    }
    return options;
  }, [activeAssignments, detail, lockedVacancyId, lockedVacancyTitle]);

  const selectedVacancyId = useMemo(() => {
    if (lockedVacancyId) return lockedVacancyId;
    if (pickedVacancyId) return pickedVacancyId;
    if (activeAssignments.length === 1) return activeAssignments[0].vacancyId;
    return '';
  }, [lockedVacancyId, pickedVacancyId, activeAssignments]);

  // Load the vacancy behind the hire so its Department / Seniority prefill the form. (HW-675)
  const { data: vacancy } = useVacancy(selectedVacancyId);

  const vacancyLocked = !!lockedVacancyId || activeAssignments.length <= 1;

  const otherActiveCount = useMemo(() => {
    if (!selectedVacancyId) return 0;
    return activeAssignments.filter((a) => a.vacancyId !== selectedVacancyId).length;
  }, [activeAssignments, selectedVacancyId]);

  const defaults = useMemo(() => {
    if (!candidate) return undefined;
    return {
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      personalEmail: candidate.email ?? undefined,
      // `null` once the vacancy has loaded but carries no department/seniority, so the
      // form resets a stale prefill to "None"; `undefined` while it is still loading.
      departmentId: vacancy ? (vacancy.department?.id ?? null) : undefined,
      seniorityLevelId: vacancy ? (vacancy.seniorityLevel?.id ?? null) : undefined,
      startDate: new Date(),
    };
  }, [candidate, vacancy]);

  const handleSubmit = (input: CreateEmployeeInput) => {
    if (!selectedVacancyId) {
      setVacancyError('Select a vacancy to hire from.');
      return;
    }
    setServerError(null);
    convertMutation.mutate(
      {
        candidateId,
        payload: {
          ...input,
          vacancyId: selectedVacancyId,
          rejectOtherAssignments: rejectOthers,
        },
      },
      {
        onSuccess: () => {
          const fullName = candidate ? `${candidate.firstName} ${candidate.lastName}` : '';
          toast.success(
            fullName
              ? `Candidate ${fullName} was successfully converted to an Employee.`
              : 'Candidate was successfully converted to an Employee.',
          );
          onOpenChange(false);
          onHired();
        },
        onError: (err) => {
          setServerError(extractErrorMessage(err, 'Failed to hire candidate'));
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const completed = convertMutation.isSuccess;
      setServerError(null);
      setVacancyError(null);
      setPickedVacancyId('');
      setRejectOthers(false);
      setFormKey((k) => k + 1);
      if (!completed) onCancelled();
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* HW-674: cap height and scroll internally so the EmployeeForm footer
          (Hire / Cancel) stays reachable on short viewports — the dialog is
          vertically centred (dialog.tsx), so an over-tall form otherwise
          overflows off both edges with no scrollbar. */}
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-lg">Hire Candidate</DialogTitle>
          <DialogDescription className="text-sm">
            Convert this candidate into an employee. The candidate will be marked Hired for the selected vacancy.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="hire-vacancy" className="text-xs font-medium text-muted-foreground">
            Vacancy <span className="text-destructive">*</span>
          </Label>
          {candidateLoading ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <Select
              value={selectedVacancyId}
              onValueChange={(value) => {
                setPickedVacancyId(value);
                setVacancyError(null);
              }}
              disabled={vacancyLocked || convertMutation.isPending}
            >
              <SelectTrigger id="hire-vacancy" className="h-9">
                <SelectValue placeholder="Select a vacancy" />
              </SelectTrigger>
              <SelectContent>
                {vacancyOptions.map((o) => (
                  <SelectItem
                    key={o.vacancyId}
                    value={o.vacancyId}
                    className="[&>span:last-child]:truncate"
                    title={o.vacancyTitle}
                  >
                    {o.vacancyTitle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {vacancyError && (
            <p className="text-xs text-destructive" role="alert">
              {vacancyError}
            </p>
          )}
        </div>

        <EmployeeForm
          key={formKey}
          defaults={defaults}
          showPersonalEmail
          requireManager
          onSubmit={handleSubmit}
          onCancel={() => handleOpenChange(false)}
          isSubmitting={convertMutation.isPending}
          serverError={serverError}
          submitLabel="Hire"
          extraSection={
            otherActiveCount > 0 ? (
              <label
                htmlFor="rejectOthers"
                className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground"
              >
                <Checkbox
                  id="rejectOthers"
                  checked={rejectOthers}
                  onCheckedChange={(v) => setRejectOthers(v === true)}
                />
                Also reject {otherActiveCount === 1 ? 'the other open application' : `${otherActiveCount} other open applications`}
              </label>
            ) : null
          }
        />
      </DialogContent>
    </Dialog>
  );
}
